
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { PackageItem } from '@/types/package-item';

const fetchInclusions = async () => {
  const { data, error } = await supabase
    .from('package_inclusions')
    .select('*')
    .order('created_at', { ascending: false });
  if (error) throw new Error(error.message);
  return data;
};

const addInclusion = async (item: Pick<PackageItem, 'text_ar' | 'text_en'>) => {
  const { data, error } = await supabase
    .from('package_inclusions')
    .insert([item])
    .select();
  if (error) throw new Error(error.message);
  return data[0];
};

const updateInclusion = async (item: Pick<PackageItem, 'id' | 'text_ar' | 'text_en'>) => {
  const { id, ...updateData } = item;
  const { data, error } = await supabase
    .from('package_inclusions')
    .update({ ...updateData, updated_at: new Date().toISOString() })
    .eq('id', id)
    .select();
  if (error) throw new Error(error.message);
  return data[0];
};

const deleteInclusion = async (id: string) => {
  const { error } = await supabase
    .from('package_inclusions')
    .delete()
    .eq('id', id);
  if (error) throw new Error(error.message);
  return id;
};

export const usePackageInclusions = () => {
  const queryClient = useQueryClient();

  const { data: inclusions, isLoading, error } = useQuery<PackageItem[]>({
    queryKey: ['package_inclusions'],
    queryFn: fetchInclusions,
  });

  const mutationOptions = {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['package_inclusions'] });
    },
  };

  const addMutation = useMutation({
    mutationFn: addInclusion,
    ...mutationOptions,
  });

  const updateMutation = useMutation({
    mutationFn: updateInclusion,
    ...mutationOptions,
  });

  const deleteMutation = useMutation({
    mutationFn: deleteInclusion,
    ...mutationOptions,
  });

  return {
    inclusions,
    isLoading,
    error,
    addInclusion: addMutation.mutateAsync,
    updateInclusion: updateMutation.mutateAsync,
    deleteInclusion: deleteMutation.mutateAsync,
    isAdding: addMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
  };
};
